import { useEffect, useRef, useState } from 'react';
import { ClipboardList, Truck, FileCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OrderStepperProps {
  currentStep: 1 | 2 | 3;
}

const stepKeys = [
  { key: 'order_details', fallback: 'Order Details', icon: ClipboardList },
  { key: 'select_carrier', fallback: 'Select Carrier', icon: Truck },
  { key: 'summary', fallback: 'Summary', icon: FileCheck },
];

const STORAGE_KEY = 'order-stepper-prev-step';

const OrderStepper = ({ currentStep }: OrderStepperProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const target = ((currentStep - 1) / (stepKeys.length - 1)) * 100;

  const prevStep = useRef<number>(
    parseInt(sessionStorage.getItem(STORAGE_KEY) || String(currentStep), 10),
  );
  const initial = ((prevStep.current - 1) / (stepKeys.length - 1)) * 100;
  const [progress, setProgress] = useState(Math.max(0, Math.min(100, initial)));

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, String(currentStep));
    const timeout = setTimeout(() => setProgress(target), 80);
    return () => clearTimeout(timeout);
  }, [target, currentStep]);

  return (
    <div className='mb-6'>
      <div className='relative h-1.5 w-full rounded-full bg-sd-muted overflow-hidden'>
        <div
          className={`absolute inset-y-0 rounded-full bg-sd-primary transition-all duration-700 ease-out ${isRTL ? 'right-0' : 'left-0'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className='flex justify-between mt-2.5'>
        {stepKeys.map((step, i) => {
          const stepNum = i + 1;
          const Icon = step.icon;
          return (
            <span
              key={step.key}
              className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                currentStep >= stepNum ? 'text-sd-foreground' : 'text-sd-muted-foreground'
              }`}
            >
              <Icon className='h-3.5 w-3.5' strokeWidth={1.5} />
              {String(t(step.key, step.fallback))}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default OrderStepper;
